/**
 * Shared hook for persisting onboarding state to the backend.
 *
 * Replaces the duplicated PATCH-to-/onboarding/state logic that was
 * previously copy-pasted across ActivationDriver, OnboardingChecklist,
 * OnboardingPill, and PreFormInviteDriver.
 *
 * Features:
 *   - 3 retries with exponential backoff (1s → 2s → 4s)
 *   - Retries on both network errors AND 5xx server errors
 *   - Returns { ok, data, error, status } so callers can decide
 *     whether to roll back optimistic state or show a toast
 *
 * Usage:
 *   const persistStep = usePersistOnboardingState();
 *   const result = await persistStep({ onboardingCurrentStep: 'share' });
 *   if (!result.ok) { // handle failure }
 */

import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { getAuthToken } from '../../../utils/auth';
import { accountsClient } from '../../../lib/trpc/accounts';

const RETRY_CONFIG = {
  retries: 3,
  retryDelay: 1000,
  backoff: 'exponential',
  retryOn5xx: true,
  context: '[onboarding] persist state',
};

export default function usePersistOnboardingState() {
  return useCallback(async (updates) => {
    const result = await accountsClient.requestJson(
      'PATCH /onboarding/state',
      { authorization: await getAuthToken(), input: updates },
      RETRY_CONFIG,
    );

    if (!result.ok) {
      console.error('[onboarding] persist failed after retries:', result.error);
      Sentry.captureException(result.error || new Error('Onboarding state persistence failed'), {
        tags: {
          area: 'onboarding',
          operation: 'persist_state',
        },
        extra: {
          status: result.status,
          updates,
        },
      });
    }
    return result;
  }, []);
}
